import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { downloadJson } from '../../utils/fileUtils';
import CodexAttributesView from './CodexAttributesView';

/**
 * CodexAttributes - Main component for managing codex attributes
 * 
 * @param {object} props
 * @param {object} props.codex - The codex data object
 * @param {string} props.codexId - The codex ID
 * @param {boolean} props.canEdit - Whether the user can edit codex attributes
 * @param {function} props.onUpdate - Function to call when attributes are updated
 * @param {boolean} props.unsavedChanges - Whether there are unsaved changes
 * @param {function} props.setUnsavedChanges - Function to update unsaved changes state
 */
const CodexAttributes = ({ codex, codexId, canEdit, onUpdate, unsavedChanges, setUnsavedChanges }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [jsonErrorFields, setJsonErrorFields] = useState(new Set());
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [resetKey, setResetKey] = useState(0); // Key to force component reset

  // Initialize or reset data when codex changes
  useEffect(() => {
    if (!codex?.attributeValues) return;
    
    // Only reset data if we're not in the middle of saving
    if (!isSaving) {
      setEditedData(JSON.parse(JSON.stringify(codex.attributeValues)));
      
      // Only clear errors when not editing (prevents disruptive UI changes)
      if (!isEditing) {
        setJsonErrorFields(new Set());
      }
      
      // Reset unsaved changes when data is reset
      setUnsavedChanges(false);
    }
  }, [codex?.attributeValues, isSaving, isEditing, setUnsavedChanges]);

  // Track unsaved changes by comparing edited data with original
  useEffect(() => {
    if (!isEditing || !editedData || !codex?.attributeValues) return;
    
    const hasChanges = JSON.stringify(editedData) !== JSON.stringify(codex.attributeValues);
    setUnsavedChanges(hasChanges);
  }, [editedData, codex?.attributeValues, isEditing, setUnsavedChanges]);

  // Toggle edit mode
  const handleToggleEdit = () => {
    if (isEditing) {
      // Cancel editing - reset everything and force component re-render
      setEditedData(JSON.parse(JSON.stringify(codex.attributeValues)));
      setJsonErrorFields(new Set());
      setError(null);
      setResetKey(prev => prev + 1); // Force reset of child components
      setUnsavedChanges(false); // Clear unsaved changes when canceling
    }
    setIsSaving(false);
    setIsEditing(!isEditing);
  };

  // Save changes
  const handleSave = async () => {
    if (jsonErrorFields.size > 0) {
      setError('Cannot save: Invalid JSON syntax detected');
      return;
    }

    setIsSaving(true);
    try {
      await onUpdate(editedData);
      setIsEditing(false);
      setJsonErrorFields(new Set());
      setError(null);
      setResetKey(prev => prev + 1); // Reset components after successful save
      setUnsavedChanges(false); // Clear unsaved changes after successful save
    } catch (err) {
      setError('Failed to update codex attributes');
    } finally {
      setIsSaving(false);
    }
  };

  // Update data safely
  const handleDataUpdate = useCallback(newData => {
    if (!isSaving && newData) {
      setEditedData(newData);
    }
  }, [isSaving]);

  // Track JSON errors
  const handleJsonErrorUpdate = useCallback((field, hasError) => {
    setJsonErrorFields(prev => {
      const updated = new Set(prev);
      hasError ? updated.add(field) : updated.delete(field);
      return updated;
    });
  }, []);

  // Early return if no codex
  if (!codex) return null;

  const attributeDefs = (codex.codexType?.attributes || []).reduce((acc, a) => {
    acc[a.code] = { dataType: a.dataType, schema: a.schema, description: a.description };
    return acc;
  }, {});
  const validations = codex.attributeValidations || {};

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with action buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 2,
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        <Typography variant="h6">
          Attributi {isEditing ? '(Editing)' : ''}
        </Typography>
        
        {canEdit && (
          <Box>
            {isEditing ? (
              <>
                <Tooltip title={jsonErrorFields.size > 0 ? "Cannot save: Invalid JSON syntax" : "Save"}>
                  <span>
                    <IconButton 
                      color="primary" 
                      onClick={handleSave}
                      disabled={jsonErrorFields.size > 0 || isSaving}
                    >
                      <SaveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton 
                    color="error" 
                    onClick={handleToggleEdit} 
                    disabled={isSaving}
                  >
                    <CancelIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="Edit Attributes">
                  <IconButton onClick={handleToggleEdit}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download JSON">
                  <IconButton onClick={() => downloadJson(codex, codexId)}>
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        )}
      </Box>

      {/* Error messages */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
          {error}
        </Alert>
      )}

      {jsonErrorFields.size > 0 && (
        <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
          Invalid JSON syntax in: {Array.from(jsonErrorFields).join(', ')}
        </Alert>
      )}
      
      {/* Attributes content with key for forced reset */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        <CodexAttributesView
          key={`attributes-${resetKey}`} // Force re-render when resetKey changes
          data={isEditing ? editedData : codex.attributeValues}
          codexId={codexId}
          isEditing={isEditing}
          onUpdate={handleDataUpdate}
          onJsonError={handleJsonErrorUpdate}
          attributeDefs={attributeDefs}
          validations={validations}
        />
      </Box>
    </Paper>
  );
};

export default CodexAttributes; 